/**
 * Admin Analytics Hooks
 *
 * React Query hooks for the admin-gated analytics endpoints served by
 * `routes/workflows/admin-analytics-workflow.ts`. These wrap the
 * /api/admin/analytics/* routes that aggregate courses, AI chat, and
 * user-behavior data using the service-role Supabase client.
 *
 * The existing `useAnalytics.ts` hooks hit Supabase via PostgREST (which
 * is RLS-gated to zero rows for analytics tables) — those still work for
 * the GA4 / conversions surface that depends on the SECURITY DEFINER RPC.
 * Anything new that needs raw analytics reads goes through the backend.
 */
import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/lib/axios";

export type AnalyticsRange = "7d" | "30d" | "90d";

const DEFAULT_STALE_MS = 5 * 60 * 1000;

export interface AdminOverview {
  range: AnalyticsRange;
  since: string;
  page_views: number;
  sessions: number;
  conversions: number;
  new_users: number;
  conversion_rate_pct: number;
}

export interface AdminCourses {
  range: AnalyticsRange;
  since: string;
  new_enrollments: number;
  course_completions: number;
  lesson_completions: number;
  purchase_count: number;
  revenue: Array<{
    currency: string;
    amount_cents: number;
    purchases: number;
  }>;
  top_courses: Array<{
    course_id: string;
    title: string;
    enrollments: number;
  }>;
  daily_enrollments: Array<{ date: string; enrollments: number }>;
}

export interface AdminChatbar {
  range: AnalyticsRange;
  since: string;
  conversations: number;
  messages: number;
  active_users: number;
  avg_messages_per_conversation: number;
  messages_by_role: Record<string, number>;
  conversations_by_scope: Array<{ scope: string; count: number }>;
  daily_conversations: Array<{ date: string; conversations: number }>;
}

export interface AdminUsers {
  range: AnalyticsRange;
  since: string;
  new_signups: number;
  total_users: number;
  active_users: number;
  authed_sessions: number;
  anon_sessions: number;
  daily_signups: Array<{ date: string; signups: number }>;
}

export function useAdminOverview(range: AnalyticsRange) {
  return useQuery({
    queryKey: ["admin-analytics", "overview", range],
    queryFn: async () => {
      const { data } = await apiClient.get<AdminOverview>(
        "/api/admin/analytics/overview",
        { params: { range } },
      );
      return data;
    },
    staleTime: DEFAULT_STALE_MS,
  });
}

export function useAdminCourses(range: AnalyticsRange) {
  return useQuery({
    queryKey: ["admin-analytics", "courses", range],
    queryFn: async () => {
      const { data } = await apiClient.get<AdminCourses>(
        "/api/admin/analytics/courses",
        { params: { range } },
      );
      return data;
    },
    staleTime: DEFAULT_STALE_MS,
  });
}

export function useAdminChatbar(range: AnalyticsRange) {
  return useQuery({
    queryKey: ["admin-analytics", "chatbar", range],
    queryFn: async () => {
      const { data } = await apiClient.get<AdminChatbar>(
        "/api/admin/analytics/chatbar",
        { params: { range } },
      );
      return data;
    },
    staleTime: DEFAULT_STALE_MS,
  });
}

export function useAdminUsers(range: AnalyticsRange) {
  return useQuery({
    queryKey: ["admin-analytics", "users", range],
    queryFn: async () => {
      const { data } = await apiClient.get<AdminUsers>(
        "/api/admin/analytics/users",
        { params: { range } },
      );
      return data;
    },
    staleTime: DEFAULT_STALE_MS,
  });
}

export interface AdminSubscribers {
  range: AnalyticsRange;
  since: string;
  now: string;
  active_subscribers: number;
  trialing_subscribers: number;
  past_due_subscribers: number;
  canceled_in_range: number;
  new_subscribers_in_range: number;
  total_subscriptions: number;
  mrr_cents: number;
  cadence: { monthly: number; yearly: number; other: number };
  by_status: Array<{ status: string; count: number }>;
  by_plan: Array<{
    subscription_type_id: string;
    name: string;
    display_name: string;
    price_monthly: number;
    price_yearly: number;
    active_count: number;
    monthly_count: number;
    yearly_count: number;
  }>;
  daily_new_subscriptions: Array<{ date: string; count: number }>;
  recent_subscribers: Array<{
    subscription_id: string;
    user_id: string | null;
    full_name: string | null;
    email: string | null;
    plan_name: string | null;
    status: string | null;
    billing_cycle: string | null;
    created_at: string;
    current_period_start: string | null;
    current_period_end: string | null;
    canceled_at: string | null;
    trial_end: string | null;
    last_payment_amount: number | null;
    last_payment_date: string | null;
  }>;
}

export function useAdminSubscribers(range: AnalyticsRange) {
  return useQuery({
    queryKey: ["admin-analytics", "subscribers", range],
    queryFn: async () => {
      const { data } = await apiClient.get<AdminSubscribers>(
        "/api/admin/analytics/subscribers",
        { params: { range } },
      );
      return data;
    },
    staleTime: DEFAULT_STALE_MS,
  });
}

export function formatCents(amountCents: number, currency: string): string {
  const amount = amountCents / 100;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency.toUpperCase()} ${amount.toFixed(2)}`;
  }
}
